/**
 * API Service - Comunicação com o backend RESTful
 * Comunicação com backend (auth + recursos)
 */
import { APP_CONFIG } from '@config/constants'

const REQUEST_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT_MS || 15000)
const SYNC_REQUEST_TIMEOUT = Number(import.meta.env.VITE_SYNC_API_TIMEOUT_MS || 180000)
const DEADLINE_SIMULATION_TIMEOUT = Number(import.meta.env.VITE_DEADLINE_SIMULATION_TIMEOUT_MS || 210000)
const REPORT_EXPORT_TIMEOUT = Math.max(SYNC_REQUEST_TIMEOUT, 600000)
let refreshInFlight = null

function getStoredSessionIdentity() {
  try {
    const user = JSON.parse(localStorage.getItem(APP_CONFIG.STORAGE_KEY) || 'null')
    return {
      deviceId: localStorage.getItem(APP_CONFIG.DEVICE_ID_STORAGE_KEY),
      userId: user?.id,
    }
  } catch {
    return { deviceId: null, userId: null }
  }
}

function normalizePayload(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  return payload
}

const ApiService = {
  BASE: APP_CONFIG.API_BASE,

  getAuthToken() {
    return null
  },

  setAuthToken(_token, _expiresInSeconds = APP_CONFIG.SESSION_TTL_MINUTES * 60) {
    // Token fica centralizado no cookie HttpOnly gerado pelo backend.
  },

  clearAuthToken() {
    // Token fica centralizado no cookie HttpOnly gerado pelo backend.
  },

  async request(endpoint, options = {}) {
    const timeoutMs = Number(options.timeoutMs || REQUEST_TIMEOUT)
    const fetchOptions = { ...options }
    delete fetchOptions.timeoutMs
    delete fetchOptions.retryOnAuthFailure
    delete fetchOptions.responseType
    delete fetchOptions.fallbackErrorMessages

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    const cancel = () => controller.abort()
    options.signal?.addEventListener('abort', cancel, { once: true })
    if (options.signal?.aborted) controller.abort()

    try {
      const headers = {
        ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      }

      const response = await fetch(`${this.BASE}${endpoint}`, {
        ...fetchOptions,
        credentials: 'include',
        headers,
        signal: controller.signal,
      })

      const canRefreshAndRetry = response.status === 401
        && options.retryOnAuthFailure !== false
        && !endpoint.startsWith('auth/')

      if (canRefreshAndRetry) {
        const refreshResult = await this.refreshToken()
        if (refreshResult.success) {
          return this.request(endpoint, { ...options, retryOnAuthFailure: false })
        }

        localStorage.removeItem(APP_CONFIG.STORAGE_KEY)
        localStorage.removeItem(APP_CONFIG.ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY)
        window.dispatchEvent(new Event('auth:session-expired'))
      }

      if (response.status === 401 && !endpoint.startsWith('auth/') && options.retryOnAuthFailure === false) {
        localStorage.removeItem(APP_CONFIG.STORAGE_KEY)
        localStorage.removeItem(APP_CONFIG.ACCESS_TOKEN_EXPIRES_AT_STORAGE_KEY)
        window.dispatchEvent(new Event('auth:session-expired'))
      }

      if (options.method === 'DELETE' && response.status === 204) {
        return { success: true, data: null }
      }

      if (response.ok && options.responseType === 'blob') {
        return { success: true, data: await response.blob(), headers: response.headers }
      }

      let payload = null
      const contentType = response.headers.get('content-type') || ''
      if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
        payload = await response.json()
      }

      if (!response.ok) {
        const error = new Error(payload?.detail || payload?.message || payload?.error || options.fallbackErrorMessages?.[response.status] || `HTTP ${response.status}: ${response.statusText}`)
        error.status = response.status
        if (payload?.geracao && typeof payload.geracao === 'object') error.geracao = payload.geracao
        if (typeof payload?.code === 'string') error.code = payload.code
        throw error
      }

      return {
        success: true,
        data: payload,
        items: normalizePayload(payload),
      }
    } catch (error) {
      const message = error.name === 'AbortError' ? (options.signal?.aborted ? 'Operação cancelada.' : 'Tempo de requisição excedido') : error.message
      if (!options.signal?.aborted) console.error(`API Error [${endpoint}]:`, error)
      return { success: false, error: message, ...(error.status ? { status: error.status } : {}),
        ...(error.geracao ? { geracao: error.geracao } : {}),
        ...(error.code ? { code: error.code } : {}) }
    } finally {
      clearTimeout(timeoutId)
      options.signal?.removeEventListener('abort', cancel)
    }
  },

  async requestBlob(endpoint, options = {}) {
    const timeoutMs = Number(options.timeoutMs || REQUEST_TIMEOUT)
    const fetchOptions = { ...options }
    delete fetchOptions.timeoutMs

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const headers = {
        ...(options.headers || {}),
      }

      const response = await fetch(`${this.BASE}${endpoint}`, {
        ...fetchOptions,
        credentials: 'include',
        headers,
        signal: controller.signal,
      })

      if (!response.ok) {
        const message = `HTTP ${response.status}: ${response.statusText}`
        throw new Error(message)
      }

      const blob = await response.blob()
      return { success: true, data: blob }
    } catch (error) {
      const message = error.name === 'AbortError' ? 'Tempo de requisição excedido' : error.message
      console.error(`API Blob Error [${endpoint}]:`, error)
      return { success: false, error: message }
    } finally {
      clearTimeout(timeoutId)
    }
  },

  async login(email, senha, deviceId = null) {
    return this.request('auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        senha,
        ...(deviceId ? { deviceId } : {}),
      }),
    })
  },

  async refreshToken({ deviceId = null, userId = null } = {}) {
    const storedIdentity = getStoredSessionIdentity()
    const payload = {
      ...(deviceId || storedIdentity.deviceId ? { deviceId: deviceId || storedIdentity.deviceId } : {}),
      ...(userId || storedIdentity.userId ? { userId: userId || storedIdentity.userId } : {}),
    }

    if (refreshInFlight) return refreshInFlight

    refreshInFlight = this.request('auth/refresh', {
      method: 'POST',
      credentials: 'include',
      timeoutMs: Math.max(REQUEST_TIMEOUT, 30000),
      retryOnAuthFailure: false,
      ...(Object.keys(payload).length ? { body: JSON.stringify(payload) } : {}),
    }).finally(() => {
      refreshInFlight = null
    })

    return refreshInFlight
  },

  async getCurrentAuthenticatedUser() {
    return this.request('auth/me', { retryOnAuthFailure: false })
  },

  async submitFormularioPublico(data) {
    return this.request('formulario-publico/enviar', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getFichasFormularioPublico() {
    return this.request('formulario-publico/fichas')
  },

  async getFichaFormularioPublico(id) {
    return this.request(`formulario-publico/fichas/${id}`)
  },

  async deleteFichaFormularioPublico(id) {
    return this.request(`formulario-publico/fichas/${id}`, { method: 'DELETE' })
  },

  async getConfiguracaoFormularioPublico() {
    return this.request('formulario-publico/configuracao')
  },

  async updateConfiguracaoFormularioPublico(data) {
    return this.request('formulario-publico/configuracao', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async updateSelecaoTecnica(id, decisao) {
    return this.request(`formulario-publico/fichas/${id}/selecao-tecnica`, {
      method: 'PUT',
      body: JSON.stringify({ decisao }),
    })
  },

  async getAreasProvaTecnica() {
    return this.request('formulario-publico/provas-tecnicas/areas')
  },

  async getProvasTecnicasFicha(id) {
    return this.request(`formulario-publico/fichas/${id}/provas-tecnicas`)
  },

  async gerarProvaTecnica(id, area) {
    return this.request(`formulario-publico/fichas/${id}/provas-tecnicas`, {
      method: 'POST',
      body: JSON.stringify({ area }),
    })
  },

  async revogarProvaTecnica(provaId) {
    return this.request(`formulario-publico/provas-tecnicas/${provaId}`, { method: 'DELETE' })
  },

  async getProvaTecnicaPublica(token) {
    return this.request(`formulario-publico/prova/${encodeURIComponent(token)}`, { retryOnAuthFailure: false })
  },

  async enviarProvaTecnicaPublica(token, data) {
    return this.request(`formulario-publico/prova/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify(data),
      retryOnAuthFailure: false,
    })
  },

  // =====================
  // Relatórios de processos
  // =====================
  async getProcessReportClients() {
    return this.request('relatorios/processos/clientes')
  },

  async getProcessReportDashboard(filters = {}) {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
    })
    return this.request(`relatorios/processos/dashboard?${params.toString()}`)
  },

  async exportProcessReportExcel(filters = {}, incluirObservacoesInternas = false) {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
    })
    params.set('incluirObservacoesInternas', String(incluirObservacoesInternas))
    return this.requestBlob(`relatorios/processos/export/xlsx?${params.toString()}`, { timeoutMs: REPORT_EXPORT_TIMEOUT })
  },

  async getClickBusReportDashboard(filters = {}) {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
    })
    return this.request(`relatorios/clickbus/dashboard?${params.toString()}`, { timeoutMs: SYNC_REQUEST_TIMEOUT })
  },

  async exportClickBusReportExcel(filters = {}) {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
    })
    return this.requestBlob(`relatorios/clickbus/export/xlsx?${params.toString()}`, { timeoutMs: SYNC_REQUEST_TIMEOUT })
  },

  // =====================
  // Prazos
  // =====================
  async getPrazos(options = {}, pageArg = 1) {
    const params = new URLSearchParams()
    const normalizedOptions = typeof options === 'number' ? { limit: options, page: pageArg } : options
    const { limit = 1500, page = 1, ...filters } = normalizedOptions || {}

    params.set('limit', String(limit))
    params.set('page', String(page))

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value))
      }
    })

    return this.request(`prazos?${params.toString()}`)
  },

  async getPrazoById(id) {
    return this.request(`prazos/${id}`)
  },

  async createPrazo(data) {
    return this.request('prazos', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async updatePrazo(id, data) {
    return this.request(`prazos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async patchPrazo(id, data) {
    return this.request(`prazos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  async deletePrazo(id) {
    return this.request(`prazos/${id}`, {
      method: 'DELETE',
    })
  },

  async exportKanbanExcel(filters = {}) {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    })

    const query = params.toString()
    const endpoint = `prazos/export/xlsx${query ? `?${query}` : ''}`
    // O XLSX do Kanban inclui as publicações vinculadas e pode exceder o timeout
    // padrão de navegação em bases maiores. Mantém o mesmo limite das exportações
    // de relatórios, sem abortar uma geração ainda em andamento no backend.
    return this.requestBlob(endpoint, { timeoutMs: REPORT_EXPORT_TIMEOUT })
  },

  async sincronizarLegalManager() {
    return this.request('legal-manager/sincronizar', {
      method: 'POST',
      timeoutMs: SYNC_REQUEST_TIMEOUT,
    })
  },

  async simulateDeadline(payload) {
    return this.request('deadline-engine/v1/simulations', {
      method: 'POST',
      body: JSON.stringify(payload),
      timeoutMs: DEADLINE_SIMULATION_TIMEOUT,
    })
  },

  async analyzeDeadline(payload) {
    return this.request('deadline-analysis/analyze', {
      method: 'POST',
      body: JSON.stringify(payload),
      timeoutMs: DEADLINE_SIMULATION_TIMEOUT,
    })
  },

  async getDeadlineAnalysis(id) {
    return this.request(`deadline-analysis/${id}`)
  },

  async submitDeadlineFeedback(payload) {
    return this.request('deadline-engine/v1/feedback', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async submitDeadlineAiValidationFeedback(simulationId, payload) {
    return this.request(`deadline-engine/v1/ai-validations/${simulationId}/feedback`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async getDeadlineAiValidationRuntime() {
    return this.request('deadline-engine/v1/ai-validations/runtime')
  },

  async getDeadlineRagCoverage() {
    return this.request('deadline-engine/v1/ai-validations/rag-sources/coverage')
  },

  async reindexDeadlineRagSources(payload) {
    return this.request('deadline-engine/v1/ai-validations/rag-sources/reindex', {
      method: 'POST',
      body: JSON.stringify(payload),
      timeoutMs: DEADLINE_SIMULATION_TIMEOUT,
    })
  },

  async updateDeadlineAiValidationRuntime(enabled, samplingRate) {
    return this.request('deadline-engine/v1/ai-validations/runtime', {
      method: 'PUT',
      body: JSON.stringify({ enabled, samplingRate }),
    })
  },

  async importPrazos(prazos) {
    return this.request('prazos/import', {
      method: 'POST',
      body: JSON.stringify(prazos),
      timeoutMs: SYNC_REQUEST_TIMEOUT,
    })
  },

  async getDeadlineLearningExamples() {
    return this.request('deadline-engine/v1/learning/examples')
  },

  async getDeadlineAdminLearningExamples(params = {}) {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, String(value))
    })
    return this.request(`deadline-engine/v1/admin/learning/examples?${query.toString()}`)
  },

  async getDeadlineAdminLearningExample(id) {
    return this.request(`deadline-engine/v1/admin/learning/examples/${id}`)
  },

  async validateDeadlineAdminLearningExample(id, payload) {
    return this.request(`deadline-engine/v1/admin/learning/examples/${id}/validate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async correctDeadlineAdminLearningExample(id, payload) {
    return this.request(`deadline-engine/v1/admin/learning/examples/${id}/validation`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  },

  async getDeadlineLearningStats() {
    return this.request('deadline-engine/v1/learning/stats')
  },

  async importPublicHistoricalLearning() {
    return this.request('deadline-engine/v1/admin/learning/import-public-historical', {
      method: 'POST',
    })
  },

  async getDeadlineEngineConfigurations() {
    return this.request('deadline-engine/v1/admin/configurations')
  },

  async createDeadlineEngineConfiguration(category, payload) {
    return this.request(`deadline-engine/v1/admin/configurations/${category}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async updateDeadlineEngineConfiguration(category, id, payload) {
    return this.request(`deadline-engine/v1/admin/configurations/${category}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  },

  async prepareDeadlinePhrasePatternPromotion(id) {
    return this.request(`deadline-engine/v1/admin/configurations/action_phrase_patterns/${id}/promotion`)
  },

  async promoteDeadlinePhrasePattern(id, payload) {
    return this.request(`deadline-engine/v1/admin/configurations/action_phrase_patterns/${id}/promote`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async deleteDeadlineEngineConfiguration(category, id, expectedVersion) {
    return this.request(`deadline-engine/v1/admin/configurations/${category}/${id}`, {
      method: 'DELETE',
      headers: { 'X-Deadline-Expected-Version': String(expectedVersion) },
    })
  },

  async getDeadlinePatternCandidates() {
    return this.request('deadline-engine/v1/admin/pattern-candidates')
  },

  async reviewDeadlinePatternCandidate(candidateId, payload) {
    return this.request(`deadline-engine/v1/admin/pattern-candidates/${candidateId}/review`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async approveDeadlinePatternCandidate(candidateId) {
    return this.request(`deadline-engine/v1/admin/pattern-candidates/${candidateId}/approve`, {
      method: 'POST',
    })
  },

  async rejectDeadlinePatternCandidate(candidateId) {
    return this.request(`deadline-engine/v1/admin/pattern-candidates/${candidateId}/reject`, {
      method: 'POST',
    })
  },

  async getDeadlineAdmin(endpoint, params = {}) {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value))
      }
    })

    const queryString = query.toString()
    return this.request(`${endpoint}${queryString ? `?${queryString}` : ''}`)
  },

  async createDeadlineAdmin(endpoint, payload) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async updateDeadlineAdmin(endpoint, payload) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  },

  async getSystemSettings() {
    return this.request('configuracoes-sistema')
  },

  async updateSystemSettings(data) {
    return this.request('configuracoes-sistema', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async cadastrarCredencialPje(data) {
    return this.request('api/pje/cadastrar', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getPjeToken(id) {
    return this.request(`api/pje/token/${id}`)
  },

  async getMinhaCredencialPje() {
    return this.request('api/pje/minha-credencial')
  },

  async getCredenciaisPje() {
    return this.request('api/pje/credenciais')
  },

  // =====================
  // Usuários
  // =====================

  // =====================
  // Monitoramento de publicações
  // =====================
  async getMonitoramentoConfiguracoes(limit = 100, page = 1, search = '') {
    const params = new URLSearchParams({ limit: String(limit), page: String(page) })
    if (search) params.set('search', search)
    return this.request(`publicacoes-monitoramento/configuracoes?${params.toString()}`)
  },

  async createMonitoramentoConfiguracao(data) {
    return this.request('publicacoes-monitoramento/configuracoes', { method: 'POST', body: JSON.stringify(data) })
  },

  async updateMonitoramentoConfiguracao(id, data) {
    return this.request(`publicacoes-monitoramento/configuracoes/${id}`, { method: 'PUT', body: JSON.stringify(data) })
  },

  async executarColetaManual(data) {
    return this.request('publicacoes-monitoramento/executar-manual', { method: 'POST', body: JSON.stringify(data), timeoutMs: SYNC_REQUEST_TIMEOUT })
  },

  async getMonitoramentoExecucoes(limit = 100, page = 1, clienteMonitoramentoId = null) {
    const params = new URLSearchParams({ limit: String(limit), page: String(page) })
    if (clienteMonitoramentoId) params.set('clienteMonitoramentoId', String(clienteMonitoramentoId))
    return this.request(`publicacoes-monitoramento/execucoes?${params.toString()}`)
  },

  async getMonitoramentoPublicacoes(filters = {}) {
    const params = new URLSearchParams({ limit: String(filters.limit || 100), page: String(filters.page || 1) })
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.set(k, String(v))
    })
    return this.request(`publicacoes-monitoramento/publicacoes?${params.toString()}`)
  },

  async getMonitoramentoPublicacaoDetalhe(id) {
    return this.request(`publicacoes-monitoramento/publicacoes/${id}`)
  },

  async reprocessarMonitoramentoPublicacao(id) {
    return this.request(`publicacoes-monitoramento/publicacoes/${id}/reprocessar`, { method: 'POST' })
  },

  async getDeadlineCounterOptions() {
    return this.request('deadline-engine/v1/counter/options')
  },

  async countDeadline(payload) {
    return this.request('deadline-engine/v1/counter', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async getDataJudEndpoints() {
    return this.request('datajud/endpoints')
  },

  async getDataJudProcesso(tribunal, numeroProcesso) {
    return this.request('datajud/processos/pesquisar', {
      method: 'POST',
      body: JSON.stringify({ tribunal, numeroProcesso, size: 10 }),
      timeoutMs: SYNC_REQUEST_TIMEOUT,
    })
  },

  async pesquisarDataJudProcessos(data) {
    return this.request('datajud/processos/pesquisar', {
      method: 'POST',
      body: JSON.stringify(data),
      timeoutMs: SYNC_REQUEST_TIMEOUT,
    })
  },

  async getUsuarios(limit = 5, page = 1, extraParams = {}) {
    const params = new URLSearchParams({ limit: String(limit), page: String(page) })
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value))
      }
    })
    return this.request(`usuarios?${params.toString()}`)
  },

  async getPerfis() { return this.request('perfis') },
  async createPerfil(data) { return this.request('perfis', { method: 'POST', body: JSON.stringify(data) }) },
  async updatePerfil(id, data) { return this.request(`perfis/${id}`, { method: 'PUT', body: JSON.stringify(data) }) },
  async deletePerfil(id) { return this.request(`perfis/${id}`, { method: 'DELETE' }) },

  async getUsuarioById(id) {
    return this.request(`usuarios/${id}`)
  },

  async getUsuarioByEmail(email) {
    const result = await this.request(`usuarios?email=${encodeURIComponent(email)}&limit=1`)
    if (result.success && Array.isArray(result.items)) {
      return { success: true, data: result.items }
    }

    const fallback = await this.request('usuarios?limit=200')
    if (fallback.success && Array.isArray(fallback.items)) {
      const user = fallback.items.find((u) => u.email === email)
      return user ? { success: true, data: [user] } : { success: true, data: [] }
    }

    return fallback
  },

  async createUsuario(data) {
    return this.request('usuarios', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async updateUsuario(id, data) {
    return this.request(`usuarios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async patchUsuario(id, data) {
    return this.request(`usuarios/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  async deleteUsuario(id) {
    return this.request(`usuarios/${id}`, {
      method: 'DELETE',
    })
  },

  async resetUsuarioPassword(id) {
    return this.request(`usuarios/${id}/reset-senha`, {
      method: 'POST',
    })
  },

  async postUsuarioAction(id, action, data = null) {
    return this.request(`usuarios/${id}/${action}`, {
      method: 'POST',
      ...(data ? { body: JSON.stringify(data) } : {}),
    })
  },

  async changeOwnPassword(data) {
    const attempts = [
      () => this.request('auth/alterar-senha', { method: 'POST', body: JSON.stringify(data) })
    ]

    for (const attempt of attempts) {
      const result = await attempt()
      if (result.success) return result
    }

    return { success: false, error: 'Não foi possível alterar a senha no endpoint de autenticação.' }
  },

  async changeUsuarioPassword(id, data) {
    const attempts = [
      () => this.request(`usuarios/${id}/alterar-senha`, { method: 'POST', body: JSON.stringify(data) })
    ]

    for (const attempt of attempts) {
      const result = await attempt()
      if (result.success) return result
    }

    return { success: false, error: 'Não foi possível alterar a senha do usuário.' }
  },

}


export default ApiService
